import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import useSWR, { useSWRConfig } from "swr";
import * as Yup from "yup";
import { fetcherFull, fetcherSimple } from "utils/api";
import ContentWrapper from "../../components/ui/content-wrapper";
import {
    Button,
    Card,
    CardBody,
    IconButton,
    Tooltip,
    CardHeader,
    CardFooter,
    Typography,
} from "@material-tailwind/react";
import MaterialIcon from "components/material-icon";
import Pagination from "components/pagination";
import { toast } from "react-toastify";

import {
    CreateSurveyDialog,
    CloneSurveyDialog,
    DeleteSurveyDialog,
} from "./dialogs";
import InfoDialog from "components/dialog/info-dialog";
import YouTubeEmbed from "utils/YouTubeEmbed";
import CsvExample from "assets/img/csv-example.png";

export default function ListSurvey() {
    const navigate = useNavigate();
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showCloneDialog, setShowCloneDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [surveyToDelete, setSurveyToDelete] = useState(undefined);
    const [surveyToCopy, setSurveyToCopy] = useState(undefined);
    const [stateToView, setStateToView] = useState("active");
    const [listPage, setListPage] = useState(1);

    // Client-side idempotency guard for write paths (create/clone/delete).
    // Prevents accidental double-click from triggering duplicate requests.
    // TODO(idempotency): send Idempotency-Key header once backend supports it.
    // Tracked in ../gobbyapi. This ref does NOT protect against network retry.
    const submittingRef = useRef(false);

    const { mutate: globalMutate } = useSWRConfig();

    const {
        data,
        error,
        mutate: listMutate,
    } = useSWR(
        [
            stateToView
                ? `/v2/survey?state=${stateToView}&page=${listPage}`
                : `/v2/survey?page=${listPage}`,
            "GET",
        ],
        ([path, method]) => fetcherSimple(path, method)
    );

    const { data: allSurveys } = useSWR(
        ["/v2/survey?page=1&rows=99999", "GET"],
        ([path, method]) => fetcherSimple(path, method),
    );

    const stateCounts = (allSurveys?.data || []).reduce((acc, s) => {
        acc[s.state] = (acc[s.state] || 0) + 1;
        return acc;
    }, {});

    const refreshCounts = () =>
        globalMutate(
            (key) => Array.isArray(key) && key[0]?.startsWith("/v2/survey"),
        );

    useEffect(() => {
        setListPage(1);
    }, [stateToView]);

    const handleSubmit = async (values) => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        try {
            const response = await fetcherFull("/v2/survey", "POST", values);
            if (response.name) {
                toast.success("Your new survey has been created!");
                refreshCounts();
                navigate(`/survey/${response.id}`);
            } else {
                toast.error(
                    "There has been an error creating this survey. If the problem persists please contact Gobby support.",
                );
            }
        } finally {
            submittingRef.current = false;
        }
    };

    const handleCloneDialogOpen = async (surveyId) => {
        setSurveyToCopy(surveyId);
        setShowCloneDialog(true);
    };

    const handleCloneSurvey = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setShowCloneDialog(false);
        const surveyIdCopy = surveyToCopy;
        setSurveyToCopy(undefined);
        try {
            const response = await fetcherSimple(
                `/v2/survey/${surveyIdCopy}/clone`,
                "POST",
            );
            if (response && response?.new_survey_id) {
                toast.success("The survey has been cloned!");
                refreshCounts();
                window.open(`/survey/${response.new_survey_id}`, "_blank");
            } else {
                toast.error(
                    "There has been an error cloning this survey. Please try again, if the problem persists then contact Gobby support.",
                );
            }
        } finally {
            submittingRef.current = false;
        }
    };

    const handleDeleteDialogOpen = async (surveyId) => {
        setSurveyToDelete(surveyId);
        setShowDeleteDialog(true);
    };

    const handleDeleteDialogClose = () => {
        setSurveyToDelete(undefined);
        setShowDeleteDialog(false);
    };

    const handleDeleteSurvey = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setShowDeleteDialog(false);
        const survey_id_deleting = surveyToDelete;
        setSurveyToDelete(undefined);
        try {
            if (survey_id_deleting && typeof survey_id_deleting === "number") {
                const response = await fetcherSimple(
                    `/v2/survey/${survey_id_deleting}`,
                    "DELETE",
                );
                if (response.success) {
                    listMutate({
                        ...data,
                        data: [
                            ...data.data.filter((d) => d.id !== survey_id_deleting),
                        ],
                    });
                    refreshCounts();
                    toast.success("The survey has been removed!");
                } else {
                    toast.error(
                        "There has been an error removing this survey. Please try again, if the problem persists then contact Gobby support.",
                    );
                }
            } else {
                toast.error("We could find no survey by that name to remove!");
            }
        } finally {
            submittingRef.current = false;
        }
    };

    const initialValues = {
        name: "",
        description: "",
        is_allow_anonymous: false,
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("Survey Name Required"),
        description: Yup.string().notRequired(),
    });

    const truncateChars = (text, limit = 120) => {
        return text.length <= limit
            ? text
            : text.slice(0, limit) + "...";
    };

    return (
        <>
            <ContentWrapper>
                <div className="lg:p-6 p-4">
                    <Card shadow={false} className="card mx-auto p-4">
                        <CardHeader className="card-inner-header mt-4"
                            color="transparent"
                            shadow={false}>
                            <Typography variant="h1" className="card-header-text">
                                    Survey Administration
                            </Typography>
                        </CardHeader>
                        <CardBody className="card-inner-body">
                            <div className="flex flex-wrap justify-between items-start gap-6">
                                {/* Left: Filter */}
                                <div className="w-full sm:w-auto text-left">
                                    <div className="flex flex-wrap gap-2">
                                        {[
                                            ["active", "Active"],
                                            ["draft", "Draft"],
                                            ["published", "Published"],
                                            ["paused", "Paused"],
                                            ["finished", "Finished"],
                                            ["", "All"],
                                        ].map(([value, label]) => {
                                            const count = allSurveys?.data
                                                ? value
                                                    ? stateCounts[value] ?? 0
                                                    : allSurveys.data.length
                                                : null;
                                            return (
                                                <Button
                                                    key={value || "all"}
                                                    size="sm"
                                                    variant="filled"
                                                    className={
                                                        stateToView === value
                                                            ? "btn btn--primary"
                                                            : "btn btn--secondary"
                                                    }
                                                    onClick={() =>
                                                        setStateToView(value)
                                                    }
                                                >
                                                    {count !== null
                                                        ? `${label} (${count})`
                                                        : label}
                                                </Button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Right: Buttons */}
                                <div className="flex flex-wrap gap-4 justify-end">
                                    <Button
                                        size="lg"
                                        className="btn btn--primary"
                                        onClick={() => setShowCreateDialog(true)}
                                    >
                                            Create New Survey
                                    </Button>

                                    <InfoDialog title="Survey Management Guide">
                                        <div className="text-black">
                                            <div>
                                                <Typography variant="h2" className="mb-2">
                                                    Create, view, and manage your surveys
                                                </Typography>
                                                <YouTubeEmbed
                                                    videoId="94UgdznNTIQ"
                                                    title="Create, view, and manage your surveys"
                                                />
                                            </div>
                                            <div className="mt-6">
                                                <Typography variant="h2" className="mb-2">
                                                    Survey status descriptions
                                                </Typography>
                                                <ul className="list-disc pl-6 space-y-1">
                                                    <li>
                                                        <Typography variant="paragraph">
                                                            <span className="font-semibold">Draft</span> &ndash; Surveys that are in the process of being designed and hasn&apos;t been launched yet.
                                                        </Typography>
                                                    </li>
                                                    <li>
                                                        <Typography variant="paragraph">
                                                            <span className="font-semibold">Published</span> &ndash; Surveys that have been launched but the start date has been set to start in the future. They will become active on allocated dates and times.
                                                        </Typography>
                                                    </li>
                                                    <li>
                                                        <Typography variant="paragraph">
                                                            <span className="font-semibold">Active</span> &ndash; Surveys that have been launched and are within the start and finished times set for the survey. Respondents can use an invitation link to complete surveys.
                                                        </Typography>
                                                    </li>
                                                    <li>
                                                        <Typography variant="paragraph">
                                                            <span className="font-semibold">Paused</span> &ndash; Surveys that have been paused by the Survey Administrator before the allocated end time. No further responses can be collected until it has been resumed. Survey Administrators can make limited changes to surveys such as survey timings.
                                                        </Typography>
                                                    </li>
                                                    <li>
                                                        <Typography variant="paragraph">
                                                            <span className="font-semibold">Finished</span> &ndash; A survey has reached the end time set for the survey or finished manually by the Survey Administrator at any time before the allocated end date.
                                                        </Typography>
                                                    </li>
                                                </ul>
                                            </div>
                                            <div className="mt-6">
                                                <Typography variant="h2" className="mb-2">
                                                    Add survey details
                                                </Typography>
                                                <YouTubeEmbed
                                                    videoId="a7PAZyE8Pxk"
                                                    title="Add survey details"
                                                />
                                            </div>
                                            <div className="mt-6">
                                                <Typography variant="h2" className="mb-2">
                                                    Upload Recipient ID CSV (example)
                                                </Typography>
                                                <img
                                                    src={CsvExample}
                                                    alt="CSV example screenshot"
                                                    width="100%"
                                                    className="rounded-xl my-4"
                                                />
                                            </div>
                                            <div className="mt-6">
                                                <Typography variant="h2" className="mb-2">
                                                    Create and design survey questions
                                                </Typography>
                                                <YouTubeEmbed
                                                    videoId="L8_Xgg-A6QI"
                                                    title="Create and design survey questions"
                                                />
                                            </div>
                                        </div>
                                    </InfoDialog>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 pt-8">
                                {data &&
                                        data.data &&
                                        data.data.map((survey) => (
                                            <Card
                                                key={survey.id}
                                                className="card-inner"
                                            >
                                                <CardHeader
                                                    floated={false}
                                                    shadow={false}
                                                    className="card-inner-header bg-transparent"
                                                >
                                                    <Typography variant="h6">
                                                        {survey.name}
                                                    </Typography>
                                                </CardHeader>

                                                <CardBody className="card-inner-body p-4 mx-4 min-h-32">
                                                    <Typography variant="paragraph">
                                                        {truncateChars(
                                                            survey.description,
                                                        )}
                                                    </Typography>
                                                </CardBody>

                                                <CardFooter className="flex flex-row-reverse pt-2 pr-2 pb-2 gap-2">
                                                    <Tooltip
                                                        className="font-body bg-[#2E86AB] text-white rounded shadow-lg px-2 py-1"
                                                        content="Remove the survey"
                                                        animate={{
                                                            mount: {
                                                                scale: 1,
                                                                y: 0,
                                                            },
                                                            unmount: {
                                                                scale: 0,
                                                                y: 25,
                                                            },
                                                        }}
                                                    >
                                                        <IconButton
                                                            variant="text" // no background initially
                                                            size="sm"
                                                            onClick={() =>
                                                                handleDeleteDialogOpen(
                                                                    survey.id,
                                                                )
                                                            }
                                                            className="btn-icon--danger"
                                                        >
                                                            <MaterialIcon
                                                                size="lg"
                                                                name="delete"
                                                            />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip
                                                        className="font-body bg-[#2E86AB] text-white rounded shadow-lg px-2 py-1"
                                                        content="Clone the survey"
                                                        animate={{
                                                            mount: {
                                                                scale: 1,
                                                                y: 0,
                                                            },
                                                            unmount: {
                                                                scale: 0,
                                                                y: 25,
                                                            },
                                                        }}
                                                    >
                                                        <IconButton
                                                            variant="text"
                                                            size="sm"
                                                            onClick={() =>
                                                                handleCloneDialogOpen(
                                                                    survey.id,
                                                                )
                                                            }
                                                            className="btn-icon--info"
                                                        >
                                                            <MaterialIcon
                                                                size="lg"
                                                                name="content_copy"
                                                            />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip
                                                        className="font-body bg-[#2E86AB] text-white rounded shadow-lg px-2 py-1"
                                                        content="Enter the survey editing screen"
                                                        animate={{
                                                            mount: {
                                                                scale: 1,
                                                                y: 0,
                                                            },
                                                            unmount: {
                                                                scale: 0,
                                                                y: 25,
                                                            },
                                                        }}
                                                    >
                                                        <IconButton
                                                            variant="text"
                                                            size="sm"
                                                            onClick={() =>
                                                                navigate(
                                                                    `/survey/${survey.id}`,
                                                                )
                                                            }
                                                            className="btn-icon--info"
                                                        >
                                                            <MaterialIcon
                                                                size="lg"
                                                                name="edit"
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                </CardFooter>
                                            </Card>
                                        ))}
                                {!!error && (
                                    <Card>
                                        <CardBody>Error loading data!</CardBody>
                                    </Card>
                                )}
                            </div>
                        </CardBody>
                        <CardFooter className="p-2">
                            {data && data.pager && (
                                <Pagination
                                    currentPage={data.pager.current_page}
                                    lastPage={data.pager.last_page}
                                    onPageChange={setListPage}
                                />
                            )}
                        </CardFooter>
                    </Card>
                </div>
            </ContentWrapper>

            {/* Dialogs */}
            <CreateSurveyDialog
                show={showCreateDialog}
                setShow={setShowCreateDialog}
                initialValues={initialValues}
                handleSubmit={handleSubmit}
                validationSchema={validationSchema}
            />

            <CloneSurveyDialog
                show={showCloneDialog}
                handleClose={() => setShowCloneDialog(false)}
                handleClone={handleCloneSurvey}
            />

            <DeleteSurveyDialog
                show={showDeleteDialog}
                handleClose={handleDeleteDialogClose}
                handleDelete={handleDeleteSurvey}
            />
        </>
    );
}
